import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  CornerDownRight,
  MoreHorizontal,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { TaskComment } from '../../types';
import { TaskMode } from '../../taskTypes/module';
import { formatRelativeTime } from '../../utils/relativeTime';
import { formatFieldValue, readFieldPath } from '../../utils/fieldPath';
import { ConfirmDialog } from './ConfirmDialog';
import { PortalMenu } from '../PortalMenu';
import { useReviewContext } from './ReviewContext';

// A deleted rubric requirement has no field path to focus. Keep a private
// focus token so the thread can still be opened for reply/resolve actions
// without pretending that it belongs to a neighbouring requirement.
const ORPHANED_FOCUS_PREFIX = '__orphaned_comment__:';

function orphanedFocusPath(commentId: string): string {
  return `${ORPHANED_FOCUS_PREFIX}${encodeURIComponent(commentId)}`;
}

function orphanedCommentIdFromFocusPath(path: string): string | null {
  if (!path.startsWith(ORPHANED_FOCUS_PREFIX)) return null;
  try {
    return decodeURIComponent(path.slice(ORPHANED_FOCUS_PREFIX.length));
  } catch {
    return null;
  }
}

interface CommentColumnProps {
  mode: TaskMode;
  /** All field-scoped comments (roots + replies) that should be visible to
   *  the current viewer. Filtered for LLM-pending visibility upstream. */
  comments: TaskComment[];
  currentRound: number;
}

export function CommentColumn({ mode, comments, currentRound }: CommentColumnProps) {
  const ctx = useReviewContext();
  const focusedPath = ctx.focusedFieldPath;

  // The caller (TaskShell's comment rail) owns the sticky positioning, the
  // card chrome (border, shadow), and the outer scroll container. This
  // component just renders the list-view ↔ focused-view switch inside a
  // full-height flex column, so the caller can size + position it freely.
  return (
    <div className="flex h-full flex-col">
      {focusedPath == null ? (
        <ListView mode={mode} comments={comments} currentRound={currentRound} />
      ) : (
        <FocusedView
          mode={mode}
          comments={comments}
          fieldPath={focusedPath}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List view — every root on the task, grouped by review round.
// ---------------------------------------------------------------------------

function ListView({
  comments,
  currentRound,
}: {
  mode: TaskMode;
  comments: TaskComment[];
  currentRound: number;
}) {
  const ctx = useReviewContext();
  const roots = useMemo(() => comments.filter((c) => c.parentCommentId == null), [comments]);
  const [tab, setTab] = useState<'unresolved' | 'resolved'>('unresolved');

  const unresolvedCount = useMemo(() => roots.filter((c) => !c.addressed).length, [roots]);
  const resolvedCount = useMemo(() => roots.filter((c) => c.addressed).length, [roots]);

  const filteredRoots = useMemo(
    () => roots.filter((c) => (tab === 'resolved' ? c.addressed : !c.addressed)),
    [roots, tab],
  );
  const grouped = useMemo(() => groupByRound(filteredRoots), [filteredRoots]);

  if (roots.length === 0) return null;

  return (
    <>
      <div role="tablist" aria-label="Comment status" className="flex flex-shrink-0 border-b border-gray-200 px-3">
        <TabButton
          active={tab === 'unresolved'}
          count={unresolvedCount}
          onClick={() => setTab('unresolved')}
        >
          Unresolved
        </TabButton>
        <TabButton
          active={tab === 'resolved'}
          count={resolvedCount}
          onClick={() => setTab('resolved')}
        >
          Resolved
        </TabButton>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {filteredRoots.length === 0 ? (
          <p className="py-6 text-center text-xs text-gray-500">
            No {tab} comments.
          </p>
        ) : (
          grouped.map(({ round, items }) => (
            <section key={round} className="mb-3 last:mb-0">
              <header className="mb-2 flex items-center justify-between px-1 text-xs font-medium text-gray-500">
                <span>{roundLabel(round, currentRound)}</span>
                <span>{items.length}</span>
              </header>
              <ul className="space-y-2">
                {items.map((comment) => (
                  <ListRoot
                    key={comment.id}
                    comment={comment}
                    onClick={() => {
                      const path = ctx.resolveCommentFieldPath(comment);
                      if (path) {
                        ctx.setFocusedFieldPath(path, comment.fieldAnchor);
                      } else if (comment.fieldAnchor != null || comment.fieldAnchorState === 'orphaned') {
                        ctx.setFocusedFieldPath(orphanedFocusPath(comment.id));
                      }
                    }}
                  />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </>
  );
}

function TabButton({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        '-mb-px border-b-2 px-3 py-2 text-xs font-medium transition-colors',
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-gray-500 hover:text-gray-700',
      ].join(' ')}
    >
      {children}
      <span className={`ml-1 ${active ? 'text-primary' : 'text-gray-400'}`}>
        ({count})
      </span>
    </button>
  );
}

function ListRoot({ comment, onClick }: { comment: TaskComment; onClick: () => void }) {
  const ctx = useReviewContext();
  const replies = ctx.commentsByRoot.get(comment.id) ?? [];
  const replyCount = replies.length;
  const label = authorLabel(comment, ctx.isAdminViewer, ctx.currentUserUid);
  // This root is currently blocking the writer's resubmit. Matches the
  // per-thread amber treatment inside the focused view so the same signal
  // carries through whichever entry point the writer uses.
  const isBlocking = ctx.blockedRootIds.has(comment.id);
  const targetPath = ctx.resolveCommentFieldPath(comment);
  const isOrphaned = comment.fieldAnchorState === 'orphaned'
    || (comment.fieldAnchor != null && targetPath == null);

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={[
          'block w-full rounded-md border bg-white px-3 py-2.5 text-left transition-colors',
          isBlocking
            ? 'border-amber-300 hover:border-amber-400 hover:bg-amber-50/30'
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
          isOrphaned ? 'border-red-200 hover:border-red-300 hover:bg-red-50/30' : '',
        ].join(' ')}
      >
        <div className="flex items-start gap-2">
          <Avatar label={label} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-sm font-medium text-gray-800">{label}</span>
              <LlmBadge comment={comment} canSee={ctx.canModerateLlm || ctx.isAdminViewer} />
            </div>
            <span className="text-[11px] text-gray-400">
              {formatRelativeTime(comment.createdAt)}
            </span>
          </div>
          {comment.addressed ? (
            <span className="inline-flex flex-shrink-0 items-center gap-1 text-xs text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Resolved
            </span>
          ) : (
            <span className="flex-shrink-0 text-xs text-amber-700">Unresolved</span>
          )}
        </div>
        <p className="mt-3 whitespace-pre-wrap text-sm text-gray-800">{comment.text}</p>
        {isOrphaned && (
          <p className="mt-2 rounded-md bg-red-50 px-2 py-1.5 text-[11px] font-medium text-red-800">
            This thread has no current rubric field. This rubric item may have been deleted.
          </p>
        )}
        {replyCount > 0 && (
          <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-gray-500">
            <CornerDownRight className="h-3 w-3" />
            {replyCount} {replyCount === 1 ? 'reply' : 'replies'} · click to open thread
          </p>
        )}
        {isBlocking && (
          <div className="mt-3 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>Resolve or reply to this comment to resubmit.</span>
          </div>
        )}
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Focused view — a single field's threads + composers.
// ---------------------------------------------------------------------------

function FocusedView({
  mode,
  comments,
  fieldPath,
}: {
  mode: TaskMode;
  comments: TaskComment[];
  fieldPath: string;
}) {
  const ctx = useReviewContext();
  const { resolveCommentFieldPath } = ctx;
  const orphanedCommentId = orphanedCommentIdFromFocusPath(fieldPath);
  const isOrphanedFocus = orphanedCommentId != null;
  const roots = useMemo(
    () =>
      comments
        .filter((c) => {
          if (c.parentCommentId != null) return false;
          if (isOrphanedFocus) {
            return c.id === orphanedCommentId && resolveCommentFieldPath(c) == null;
          }
          return resolveCommentFieldPath(c) === fieldPath;
        })
        .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()),
    [comments, fieldPath, isOrphanedFocus, orphanedCommentId, resolveCommentFieldPath],
  );

  // The reviewer can add NEW findings on this field without backtracking to
  // the modal; the writer can't start new root threads (same as the current
  // "canComment === review" rule).
  const canStartNewThread = mode === 'review' && !isOrphanedFocus;

  return (
    <>
      <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
        <button
          type="button"
          onClick={() => ctx.setFocusedFieldPath(null)}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-gray-600 hover:bg-gray-100"
          aria-label="Back to all comments"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All comments
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {isOrphanedFocus && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-800">
            This thread no longer matches a current rubric requirement. This rubric item may have been deleted.
          </p>
        )}
        {canStartNewThread && (
          <NewThreadComposer
            fieldPath={fieldPath}
            fieldAnchor={ctx.focusedFieldAnchor ?? undefined}
          />
        )}

        {roots.length === 0 && !canStartNewThread && (
          <p className="py-6 text-center text-xs text-gray-500">
            No comments on this field yet.
          </p>
        )}

        {roots.map((root) => (
          <Thread key={root.id} root={root} />
        ))}
      </div>
    </>
  );
}

function NewThreadComposer({
  fieldPath,
  fieldAnchor,
}: {
  fieldPath: string;
  fieldAnchor?: TaskComment['fieldAnchor'];
}) {
  const ctx = useReviewContext();
  return (
    <button
      type="button"
      onClick={() => ctx.openCommentComposer(fieldPath, fieldAnchor)}
      className="w-full rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-left text-xs text-gray-600 hover:border-primary hover:text-primary"
    >
      + Add new comment on this field…
    </button>
  );
}

function Thread({ root }: { root: TaskComment }) {
  const ctx = useReviewContext();
  const replies = ctx.commentsByRoot.get(root.id) ?? [];
  const canAddress = ctx.canResolveComments && root.parentCommentId == null;
  const isLlm = root.source === 'llm';
  const llmPending = isLlm && root.llmStatus !== 'approved';
  const showModeration = isLlm && ctx.canModerateLlm;

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [moderating, setModerating] = useState(false);
  const [diffComment, setDiffComment] = useState<TaskComment | null>(null);

  const startEdit = () => {
    setEditText(root.text);
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setEditText('');
  };
  const runModerated = async (fn: () => Promise<void> | void) => {
    if (moderating) return;
    setModerating(true);
    try {
      await fn();
    } finally {
      setModerating(false);
    }
  };
  const saveEdit = () =>
    runModerated(async () => {
      const trimmed = editText.trim();
      if (!trimmed || trimmed === root.text) {
        cancelEdit();
        return;
      }
      await ctx.editLlmComment(root.id, trimmed);
      cancelEdit();
    });

  const pending = ctx.pendingCommentId === root.id;

  // This thread is currently blocking the writer's resubmit — show an
  // inline amber footer so they see exactly which threads still need action
  // rather than hunting via a task-level banner.
  const isBlocking = ctx.blockedRootIds.has(root.id);

  return (
    <section
      className={[
        'rounded-md border bg-white px-3 py-2.5',
        isBlocking ? 'border-amber-300' : 'border-gray-200',
      ].join(' ')}
    >
      <CommentRowHeader
        comment={root}
        rightSlot={<CommentActions comment={root} onOpenDiff={setDiffComment} />}
      />

      {editing ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none"
            autoFocus
          />
          <div className="flex justify-end gap-2 text-xs">
            <button
              type="button"
              onClick={cancelEdit}
              disabled={moderating}
              className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={moderating || editText.trim().length === 0}
              className="rounded-md bg-primary px-2 py-0.5 text-white hover:bg-primary-hover disabled:opacity-50"
            >
              {moderating ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <EditableBody
          comment={root}
          className="mt-3 whitespace-pre-wrap text-sm text-gray-800"
        />
      )}

      {!editing && (root.editedByName || root.editedAt) && (
        <p className="mt-1 text-[10px] italic text-gray-400">
          Edited
          {ctx.isAdminViewer && root.editedByName ? ` by ${root.editedByName}` : ''}
          {root.editedAt ? ` · ${formatRelativeTime(root.editedAt)}` : ''}
        </p>
      )}

      {showModeration && !editing && (
        <div className="mt-2 flex flex-wrap gap-1 border-t border-gray-100 pt-2">
          {llmPending && (
            <button
              type="button"
              onClick={() => runModerated(() => ctx.approveLlmComment(root.id))}
              disabled={moderating}
              className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
            >
              {moderating ? 'Working…' : 'Approve'}
            </button>
          )}
          <button
            type="button"
            onClick={startEdit}
            disabled={moderating}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => runModerated(() => ctx.deleteLlmComment(root.id))}
            disabled={moderating}
            className="rounded-md border border-red-200 bg-white px-2 py-0.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      )}

      {replies.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
          {replies.map((reply) => (
            <li key={reply.id} className="py-3 last:pb-0">
              <CommentRowHeader
                comment={reply}
                rightSlot={<CommentActions comment={reply} onOpenDiff={setDiffComment} />}
              />
              <EditableBody
                comment={reply}
                className="mt-3 whitespace-pre-wrap text-sm text-gray-800"
              />
            </li>
          ))}
        </ul>
      )}

      {ctx.mode !== 'preview' ? (
        <ReplyComposer
          rootId={root.id}
          resolveControl={
            canAddress ? (
              <ResolveControl
                addressed={root.addressed}
                pending={pending}
                onToggle={() => ctx.markAddressed(root.id, !root.addressed)}
              />
            ) : root.addressed ? (
              <span className="text-xs text-emerald-700">Resolved</span>
            ) : null
          }
        />
      ) : (
        // In preview mode there's no reply composer to host the resolve
        // status, so surface it inline under the thread body for admins
        // auditing in-flight tasks.
        root.addressed && (
          <p className="mt-2 text-xs text-emerald-700">Resolved</p>
        )
      )}

      {isBlocking && (
        <div className="mt-3 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>Resolve or reply to this comment to resubmit.</span>
        </div>
      )}
      {diffComment && (
        <FieldDiffModal
          comment={diffComment}
          onClose={() => setDiffComment(null)}
        />
      )}
    </section>
  );
}

/**
 * Plain <p> that swaps into a textarea when the author clicks it. Only
 * the comment's own author can enter edit mode; everyone else sees read-only
 * text. Enter saves (Cmd+Enter preserves newlines inside the textarea — we
 * use the explicit Save/Cancel buttons to avoid surprise submits).
 *
 * The Firestore rule (`resource.data.authorId == uid()`) is the real gate;
 * this component only decides whether to show the affordance.
 */
function EditableBody({
  comment,
  className,
}: {
  comment: TaskComment;
  className: string;
}) {
  const ctx = useReviewContext();
  const canEdit =
    !!ctx.currentUserUid
    && comment.authorId === ctx.currentUserUid
    // LLM comments use their own moderation path (approve/edit/delete). Keep
    // this affordance for human-authored rows only — the LLM flow is already
    // visible to reviewers via the moderation row below the body.
    && comment.source !== 'llm';

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(comment.text);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // When a new snapshot lands while we're NOT editing, refresh the draft.
  // Don't clobber a live edit.
  useEffect(() => {
    if (!editing) setValue(comment.text);
  }, [comment.id, comment.text, editing]);

  const cancel = () => {
    setValue(comment.text);
    setEditing(false);
    setError(null);
  };
  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError('Comment cannot be empty.');
      return;
    }
    if (trimmed === comment.text) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await ctx.editComment(comment.id, trimmed);
      setEditing(false);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save edit.');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="mt-1 space-y-2">
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={Math.max(2, Math.min(8, value.split('\n').length))}
          autoFocus
          className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none"
        />
        {error && <p className="text-[11px] text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 text-xs">
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || value.trim().length === 0}
            className="rounded-md bg-primary px-2 py-0.5 text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <p
      className={[
        className,
        canEdit ? 'cursor-pointer rounded-sm hover:bg-gray-50' : '',
      ].filter(Boolean).join(' ')}
      title={canEdit ? 'Click to edit your comment' : undefined}
      onClick={canEdit ? () => setEditing(true) : undefined}
    >
      {comment.text}
    </p>
  );
}

function ResolveControl({
  addressed,
  pending,
  onToggle,
}: {
  addressed: boolean;
  pending: boolean;
  onToggle: () => void;
}) {
  if (addressed) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="text-emerald-700">Resolved</span>
        <button
          type="button"
          onClick={onToggle}
          disabled={pending}
          className="text-gray-400 underline hover:text-gray-600 disabled:opacity-50"
        >
          {pending ? 'Saving…' : 'Reopen'}
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={pending}
      className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
    >
      {pending ? 'Saving…' : 'Mark as resolved'}
    </button>
  );
}

function ReplyComposer({
  rootId,
  resolveControl,
}: {
  rootId: string;
  /** Optional thread-level action rendered on the far left of the action row
   *  (typically the Mark-as-resolved / Unmark control). Pairs the thread's
   *  two primary actions — resolve + reply — on a single row. */
  resolveControl?: React.ReactNode;
}) {
  const ctx = useReviewContext();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  const submit = async () => {
    const trimmed = text.trim();
    if (trimmed.length < 2) {
      setError('Write at least a couple of words.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ctx.addReply(rootId, trimmed);
      setText('');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to post reply.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3">
      <textarea
        ref={ref}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="Reply…"
        className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none"
      />
      {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
      <div className="mt-1 flex items-center justify-between gap-2">
        <div>{resolveControl}</div>
        <button
          type="button"
          onClick={submit}
          disabled={busy || text.trim().length < 2}
          className="rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-white hover:bg-primary-hover disabled:opacity-50"
        >
          {busy ? 'Posting…' : 'Reply'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

/**
 * Visible to reviewers (so they can moderate) and admins (so they can
 * audit). Hidden from pure annotators so AI and human reviewer output look
 * identical to them — the annotator experience is a uniform "Reviewer"
 * voice regardless of which reviewer produced each comment.
 */
function LlmBadge({
  comment,
  canSee,
}: {
  comment: TaskComment;
  canSee: boolean;
}) {
  if (!canSee || comment.source !== 'llm') return null;
  const approved = comment.llmStatus === 'approved';
  const tone = approved
    ? 'bg-emerald-100 text-emerald-800'
    : 'bg-violet-100 text-violet-800';
  const label = approved ? 'AI · approved' : 'AI · pending';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${tone}`}
      title={comment.model ? `Model: ${comment.model}` : undefined}
    >
      <Sparkles className="h-3 w-3" />
      {label}
    </span>
  );
}

/**
 * Anonymised author label. Admins see real names everywhere (including the
 * literal "AI Reviewer" stored on LLM comments) for auditing. Non-admins see
 * a uniform "Reviewer" voice EXCEPT when the author is the viewer themselves
 * — showing your own name on your own reply keeps the conversation legible
 * without breaking the AI/human-reviewer anonymization the annotator sees.
 */
const GENERIC_AUTHOR = 'Reviewer';
function authorLabel(
  comment: TaskComment,
  isAdminViewer: boolean,
  currentUserUid: string | null,
): string {
  if (isAdminViewer && comment.authorName) return comment.authorName;
  if (currentUserUid && comment.authorId === currentUserUid && comment.authorName) {
    return comment.authorName;
  }
  return GENERIC_AUTHOR;
}

/** Single uppercase initial from the author's first name. Deterministic so
 *  the same author always renders the same avatar. Falls back to `?` when
 *  the label is empty. */
function initialsOf(label: string): string {
  const first = label.trim().split(/\s+/)[0] ?? '';
  return first ? first[0].toUpperCase() : '?';
}

/** Circle avatar with the author's initials. Pure presentation — no auth
 *  or anonymization logic lives here; callers pass in the already-resolved
 *  label so the anonymized "Reviewer" voice stays intact for non-admins.
 *  Sized to match the combined line-height of the stacked name + time so
 *  the row reads as a single block instead of misaligned columns. */
function Avatar({ label }: { label: string }) {
  return (
    <span
      aria-hidden
      className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700"
    >
      {initialsOf(label)}
    </span>
  );
}

/**
 * Shared header for a comment row — avatar on the left, stacked name/time
 * in the middle, optional action menu on the right. Used for both root
 * comments and replies so the layout stays consistent.
 *
 * Name (`leading-tight`) and time (`leading-tight`) are pulled flush so the
 * pair vertically fits the avatar circle without extra air, and the right-
 * hand menu button's vertical center is biased toward the name row.
 */
function CommentRowHeader({
  comment,
  rightSlot,
}: {
  comment: TaskComment;
  rightSlot?: React.ReactNode;
}) {
  const ctx = useReviewContext();
  const label = authorLabel(comment, ctx.isAdminViewer, ctx.currentUserUid);
  return (
    <div className="flex items-center gap-2.5">
      <Avatar label={label} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 leading-tight">
          <span className="truncate text-sm font-medium text-gray-800">{label}</span>
          <LlmBadge comment={comment} canSee={ctx.canModerateLlm || ctx.isAdminViewer} />
        </div>
        <span className="block text-[11px] leading-tight text-gray-400">
          {formatRelativeTime(comment.createdAt)}
        </span>
      </div>
      {rightSlot && <div className="flex-shrink-0">{rightSlot}</div>}
    </div>
  );
}

function CommentActions({
  comment,
  onOpenDiff,
}: {
  comment: TaskComment;
  onOpenDiff: (comment: TaskComment) => void;
}) {
  return <CommentOverflowMenu comment={comment} onOpenDiff={onOpenDiff} />;
}

function FieldDiffModal({
  comment,
  onClose,
}: {
  comment: TaskComment;
  onClose: () => void;
}) {
  const ctx = useReviewContext();
  const currentPath = ctx.resolveCommentFieldPath(comment);
  const current = currentPath
    ? readFieldPath(ctx.currentResult, currentPath)
    : { missing: true, value: null };
  const before = formatFieldValue(
    comment.fieldSnapshot?.value,
    comment.fieldSnapshot?.missing === true,
  );
  const after = formatFieldValue(current.value, current.missing);
  const rows = buildDiffRows(before, after);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="field-diff-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[86vh] w-full max-w-5xl flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <h2 id="field-diff-title" className="text-base font-semibold text-gray-900">
              Field diff
            </h2>
            <p className="mt-1 truncate font-mono text-xs text-gray-500">
              {currentPath ?? 'No current field — thread needs reassignment'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close diff"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 border-b border-gray-200 bg-gray-50 text-xs font-semibold text-gray-600">
          <div className="border-r border-gray-200 px-3 py-2">
            Sent back
            {comment.fieldSnapshot?.reviewRound
              ? ` · round ${comment.fieldSnapshot.reviewRound}`
              : ''}
          </div>
          <div className="px-3 py-2">Current submission</div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full table-fixed border-separate border-spacing-0 text-xs">
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <DiffCell side="before" kind={row.kind} text={row.before} />
                  <DiffCell side="after" kind={row.kind} text={row.after} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DiffCell({
  side,
  kind,
  text,
}: {
  side: 'before' | 'after';
  kind: DiffRow['kind'];
  text: string | null;
}) {
  const changed =
    (kind === 'delete' && side === 'before')
    || (kind === 'insert' && side === 'after');
  const bg = changed
    ? side === 'before'
      ? 'bg-red-50 text-red-950'
      : 'bg-emerald-50 text-emerald-950'
    : 'bg-white text-gray-800';
  const prefix = changed ? (side === 'before' ? '-' : '+') : ' ';
  return (
    <td className={`w-1/2 border-b border-gray-100 align-top ${bg}`}>
      <pre className="min-h-[1.5rem] whitespace-pre-wrap break-words px-3 py-1 font-mono leading-5">
        <span className={changed ? 'select-none font-bold opacity-70' : 'select-none text-gray-300'}>
          {prefix}
        </span>
        {text ?? ''}
      </pre>
    </td>
  );
}

interface DiffRow {
  kind: 'equal' | 'delete' | 'insert';
  before: string | null;
  after: string | null;
}

function splitDiffLines(text: string): string[] {
  if (text.length === 0) return [''];
  return text.split('\n');
}

function buildDiffRows(before: string, after: string): DiffRow[] {
  const a = splitDiffLines(before);
  const b = splitDiffLines(after);
  const dp: number[][] = Array.from({ length: a.length + 1 }, () =>
    Array.from({ length: b.length + 1 }, () => 0),
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const rows: DiffRow[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      rows.push({ kind: 'equal', before: a[i], after: b[j] });
      i++;
      j++;
    } else if (j >= b.length || (i < a.length && dp[i + 1][j] >= dp[i][j + 1])) {
      rows.push({ kind: 'delete', before: a[i], after: null });
      i++;
    } else {
      rows.push({ kind: 'insert', before: null, after: b[j] });
      j++;
    }
  }
  return rows;
}

/**
 * Author-only overflow menu for a human comment. Shows a trash "Delete"
 * affordance, confirms once, calls the context's deleteComment action.
 * LLM comments use their own moderation row elsewhere — this stays silent
 * on anything the current user didn't author.
 */
function CommentOverflowMenu({
  comment,
  onOpenDiff,
}: {
  comment: TaskComment;
  onOpenDiff: (comment: TaskComment) => void;
}) {
  const ctx = useReviewContext();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const canDiff =
    ctx.canViewFieldDiffs
    && !!comment.fieldSnapshot
    && typeof comment.fieldPath === 'string'
    && comment.fieldPath.length > 0;
  const canDelete =
    !!ctx.currentUserUid
    && comment.authorId === ctx.currentUserUid
    && comment.source !== 'llm';

  if (!canDiff && !canDelete) return null;

  const runDelete = async () => {
    setBusy(true);
    try {
      await ctx.deleteComment(comment.id);
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        aria-label="Comment actions"
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <PortalMenu
          anchorRef={triggerRef}
          onRequestClose={() => setOpen(false)}
          role="menu"
          className="w-36"
        >
          {canDiff && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenDiff(comment);
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
            >
              View diff
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setConfirmOpen(true);
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          )}
        </PortalMenu>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title="Delete comment?"
        message={
          comment.parentCommentId == null
            ? 'This removes the comment and any replies beneath it. This cannot be undone.'
            : 'This removes your reply. This cannot be undone.'
        }
        confirmLabel="Delete"
        tone="danger"
        busy={busy}
        onCancel={() => {
          if (!busy) setConfirmOpen(false);
        }}
        onConfirm={runDelete}
      />
    </div>
  );
}

function groupByRound(
  comments: TaskComment[],
): Array<{ round: number; items: TaskComment[] }> {
  const map = new Map<number, TaskComment[]>();
  for (const c of comments) {
    const arr = map.get(c.reviewRound);
    if (arr) arr.push(c);
    else map.set(c.reviewRound, [c]);
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([round, items]) => ({ round, items }));
}

function roundLabel(round: number, currentRound: number): string {
  const base = round === 0 ? 'Initial review' : `Round ${round}`;
  return round === currentRound ? `${base} (current)` : base;
}
